import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { getUsersConstans } from '~/common/constants/users';
import { Button } from '~/eds/src/components/Button/Button';
import { Card } from '~/eds/src/components/Card/Card';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { PasswordInput } from '~/eds/src/components/PasswordInput/PasswordInput';
import { api } from '~/redux/api/createApi';

import { usePasswordsWithValidation } from './hooks';
import { LoginPassword } from './LoginPassword';

export const ChangePassword = ({
  isWorkdayUser,
}: {
  isWorkdayUser: boolean;
}) => {
  const { toast } = useToast();
  const [currentPassword, setCurrentPasswordChange] = useState('');
  const [
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    validationResult,
  ] = usePasswordsWithValidation();

  const intl = useIntl();
  const { WORKDAY_ADMIN_DISABLED_ACTION_MESSAGE } = getUsersConstans(intl);

  const [updatePassword, { isLoading: isUpdatingPassword }] =
    api.endpoints.updatePassword.useMutation();

  return (
    <Card
      mode="bordered"
      header={{
        title: intl.formatMessage({
          id: 'feature.ChangePassword.cardTitle',
          defaultMessage: 'Change Password',
          description: 'Title for change password card',
        }),
      }}
    >
      <Layout w="25%" preset="form-fields" align="flex-start">
        <FormField<string, false>
          name="current password"
          input={PasswordInput}
          label={intl.formatMessage({
            id: 'feature.ChangePassword.currentPasswordLabel',
            defaultMessage: 'Current Password',
            description: 'Label for current password field',
          })}
          disabled={isWorkdayUser}
          value={currentPassword}
          onChange={(currentPassword) => {
            if (!currentPassword) {
              setPassword('');
            } else {
              setCurrentPasswordChange(currentPassword);
            }
          }}
        />
        <LoginPassword
          password={password}
          setPassword={setPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          validationResult={validationResult}
          isWorkdayUser={isWorkdayUser}
        />
        <Button
          variant="primary"
          id="user_changePassword_updatePasswordButton"
          tooltip={
            isWorkdayUser
              ? WORKDAY_ADMIN_DISABLED_ACTION_MESSAGE
              : !currentPassword
                ? intl.formatMessage({
                    id: 'feature.ChangePassword.currentPasswordRequired',
                    defaultMessage: 'Current password is required',
                    description:
                      'Error message when current password is not provided',
                  })
                : !validationResult.allValid
                  ? intl.formatMessage({
                      id: 'feature.ChangePassword.validationError',
                      defaultMessage: 'Please fix the errors in the form',
                      description: 'Error message when validation fails',
                    })
                  : undefined
          }
          onClick={() => {
            updatePassword({
              currentPassword,
              newPassword: password,
              confirmNewPassword: confirmPassword,
            })
              .unwrap()
              .then(() => {
                setCurrentPasswordChange('');
                setPassword('');
                setConfirmPassword('');
                toast({
                  message: intl.formatMessage({
                    id: 'feature.ChangePassword.updateSuccessMessage',
                    defaultMessage: 'Password has been updated successfully.',
                    description: 'Success message when password is updated',
                  }),
                  status: 'success',
                });
              })
              .catch((error) => {
                toast({
                  message: error.response.data?.message,
                  status: 'danger',
                });
              });
          }}
          disabled={
            isWorkdayUser ||
            !currentPassword ||
            !validationResult.allValid ||
            isUpdatingPassword
          }
          text={intl.formatMessage({
            id: 'feature.ChangePassword.updatePasswordButton',
            defaultMessage: 'Update Password',
            description: 'Update password button text',
          })}
        />
      </Layout>
    </Card>
  );
};
